'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

import { visibleQuestions } from '@/lib/calculator/engine'
import { OptionCard } from './OptionCard'
import { EstimatePanel } from './EstimatePanel'
import { CaptureForm } from './CaptureForm'
import { BreakdownStack } from './BreakdownStack'

// The /pricing wizard's answers persist across a reload within the same
// tab so a founder mid-flow doesn't lose progress on an accidental
// refresh — but never across tabs/devices (sessionStorage, not
// localStorage), and never silently trust corrupt data (see
// loadStoredAnswers below).
const STORAGE_KEY = 'kk_calc_answers'

// How long a single-choice tap holds on its selected state before the
// wizard springs to the next question — long enough to register the tap
// visually, short enough not to feel laggy.
const SINGLE_ADVANCE_DELAY_MS = 250

const fieldGap = 'space-y-3'

// ---------------------------------------------------------------------------
// Pure helpers (component-local — engine.ts owns the server-authoritative
// visibility/pricing rules; these just walk the same config shape for
// wizard bookkeeping: ordering, "have they answered this", and resume).
// ---------------------------------------------------------------------------

function flattenQuestions(config) {
  const out = []
  for (const section of config?.sections ?? []) {
    if (!section?.key) continue
    for (const question of section?.questions ?? []) {
      if (!question?.key) continue
      out.push({ sectionKey: section.key, sectionTitle: section.title || null, question })
    }
  }
  return out
}

function isAnswered(value, type) {
  if (type === 'multi') return Array.isArray(value) && value.length > 0
  if (type === 'slider') return typeof value === 'number' && Number.isFinite(value)
  return typeof value === 'string' && value.length > 0
}

function firstUnansweredIndex(flatVisible, answers) {
  const idx = flatVisible.findIndex((fq) => !isAnswered(answers[fq.question.key], fq.question.type))
  return idx === -1 ? flatVisible.length : idx
}

// Corrupt/missing sessionStorage always falls back to a fresh {} — never
// throws, never carries garbage into wizard state.
function loadStoredAnswers() {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

export function CalculatorWizard({ config, onAnswersChange }) {
  const shouldReduceMotion = useReducedMotion()

  const flatQuestions = useMemo(() => flattenQuestions(config), [config])
  const allKnownKeys = useMemo(
    () => new Set(flatQuestions.map((fq) => fq.question.key)),
    [flatQuestions],
  )

  // Deterministic on both server and first client render (no sessionStorage
  // read here) — resume happens strictly after mount via effect below, so
  // there's nothing for hydration to mismatch against.
  const [answers, setAnswers] = useState({})
  const [stepIndex, setStepIndex] = useState(0)
  const [direction, setDirection] = useState(1)
  const [isHydrated, setIsHydrated] = useState(false)
  const [pendingAdvanceKey, setPendingAdvanceKey] = useState(null)
  // Set once CaptureForm's POST succeeds — { resultsToken, snapshot }.
  // Its presence is what swaps the completion stage from CaptureForm to
  // BreakdownStack, and hides the live EstimatePanel (the breakdown now
  // shows the real, unblurred numbers inline; a blurred teaser panel next
  // to it would be redundant/confusing).
  const [submissionResult, setSubmissionResult] = useState(null)

  const visible = useMemo(() => visibleQuestions(config, answers), [config, answers])
  const visibleSet = useMemo(() => new Set(visible), [visible])
  const visibleFlatQuestions = useMemo(
    () => flatQuestions.filter((fq) => visibleSet.has(fq.question.key)),
    [flatQuestions, visibleSet],
  )

  const visibleFlatQuestionsRef = useRef(visibleFlatQuestions)
  visibleFlatQuestionsRef.current = visibleFlatQuestions

  const total = visibleFlatQuestions.length
  const clampedStep = Math.min(stepIndex, total)
  const isComplete = total > 0 && clampedStep >= total
  const current = !isComplete ? visibleFlatQuestions[clampedStep] : null

  // ---------------------------------------------------------------------
  // Resume: one-shot, client-only. Reads sessionStorage, drops anything
  // for a question key the current config doesn't recognize (a stale
  // config edit), re-derives visibility from the SAME pure engine
  // function evaluate()/submit will use, and lands on the first
  // unanswered visible question (or the completion slot if all are
  // answered).
  // ---------------------------------------------------------------------
  useEffect(() => {
    const stored = loadStoredAnswers()
    const sanitized = {}
    for (const [key, value] of Object.entries(stored)) {
      if (allKnownKeys.has(key)) sanitized[key] = value
    }
    if (Object.keys(sanitized).length > 0) {
      const visibleNow = new Set(visibleQuestions(config, sanitized))
      const flatVisibleNow = flatQuestions.filter((fq) => visibleNow.has(fq.question.key))
      setAnswers(sanitized)
      setStepIndex(firstUnansweredIndex(flatVisibleNow, sanitized))
    }
    setIsHydrated(true)
    // Intentionally run once on mount only — this is a one-shot resume,
    // not a live sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist — gated on isHydrated so the initial {} default never
  // clobbers a not-yet-applied resume value (see the effect above; both
  // fire in the same mount commit, and effect ordering alone can't
  // guarantee this one runs after resume's setState has flushed).
  useEffect(() => {
    if (!isHydrated) return
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(answers))
    } catch {
      // sessionStorage unavailable (private mode, quota) — resume just
      // won't work next time; the wizard itself keeps working.
    }
  }, [answers, isHydrated])

  // Tamper-guard mirror: whenever a branch change hides a question, drop
  // its answer from state too, so sessionStorage (and onAnswersChange)
  // never carry an answer the engine itself would ignore.
  useEffect(() => {
    setAnswers((prev) => {
      let changed = false
      const next = {}
      for (const [key, value] of Object.entries(prev)) {
        if (visibleSet.has(key)) {
          next[key] = value
        } else {
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [visibleSet])

  // Keep stepIndex in bounds when the visible list shrinks/grows.
  useEffect(() => {
    setStepIndex((i) => Math.min(i, total))
  }, [total])

  useEffect(() => {
    onAnswersChange?.(answers)
  }, [answers, onAnswersChange])

  // Single-choice auto-advance, 250ms after the tap (0 under reduced
  // motion — no reason to hold a delay that exists purely to let a
  // spring transition read as intentional).
  useEffect(() => {
    if (!pendingAdvanceKey) return
    const timer = setTimeout(
      () => {
        setStepIndex((currentIndex) => {
          const list = visibleFlatQuestionsRef.current
          const idx = list.findIndex((fq) => fq.question.key === pendingAdvanceKey)
          return idx === -1 ? currentIndex : Math.min(idx + 1, list.length)
        })
        setPendingAdvanceKey(null)
      },
      shouldReduceMotion ? 0 : SINGLE_ADVANCE_DELAY_MS,
    )
    return () => clearTimeout(timer)
  }, [pendingAdvanceKey, shouldReduceMotion])

  function advanceFromKey(key) {
    setDirection(1)
    setStepIndex((currentIndex) => {
      const list = visibleFlatQuestionsRef.current
      const idx = list.findIndex((fq) => fq.question.key === key)
      return idx === -1 ? currentIndex : Math.min(idx + 1, list.length)
    })
  }

  function selectSingle(questionKey, optionKey) {
    setAnswers((prev) => ({ ...prev, [questionKey]: optionKey }))
    setDirection(1)
    setPendingAdvanceKey(questionKey)
  }

  function toggleMulti(questionKey, optionKey) {
    setAnswers((prev) => {
      const list = Array.isArray(prev[questionKey]) ? prev[questionKey] : []
      const next = list.includes(optionKey)
        ? list.filter((k) => k !== optionKey)
        : [...list, optionKey]
      return { ...prev, [questionKey]: next }
    })
  }

  function setSliderValue(questionKey, value) {
    setAnswers((prev) => ({ ...prev, [questionKey]: value }))
  }

  function goNext() {
    if (!current) return
    advanceFromKey(current.question.key)
  }

  function goBack() {
    setDirection(-1)
    setPendingAdvanceKey(null)
    setStepIndex((i) => Math.max(i - 1, 0))
  }

  const answeredCount = Math.min(clampedStep, total)

  if (total === 0) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 text-center text-sm text-white/60">
        No questions are configured yet.
      </div>
    )
  }

  // The EstimatePanel (sticky desktop column + mobile fixed bar/sheet)
  // stays live through the question flow AND the capture step — the
  // blurred teaser is part of what motivates filling out CaptureForm. It
  // disappears once submissionResult lands: BreakdownStack shows the same
  // items unblurred at that point, so a second, still-blurred panel next
  // to it would just be confusing.
  const showEstimatePanel = !submissionResult

  return (
    // Two-column ≥1024px: wizard card left, live estimate sticky right.
    // Below 1024px the estimate collapses into EstimatePanel's own fixed
    // bottom bar/sheet, so this wrapper is a plain single column there —
    // the bottom padding leaves room for that fixed bar to never cover the
    // Back/Next controls beneath it.
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-10">
      <div className={showEstimatePanel ? 'pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-0' : ''}>
        {/* Progress rail — answered-visible / total-visible, per section. */}
        {!submissionResult && (
          <div className="mb-5">
            <div className="flex items-center justify-between gap-3 text-xs text-neutral-500">
              <span className="min-w-0 truncate">
                {isComplete ? 'All set' : current?.sectionTitle || current?.question.prompt}
              </span>
              <span className="shrink-0" data-testid="calc-progress-count">
                {answeredCount} / {total}
              </span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <motion.div
                className="h-full rounded-full bg-kingdom-gold"
                animate={{ width: `${total ? (answeredCount / total) * 100 : 0}%` }}
                transition={
                  shouldReduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 32 }
                }
              />
            </div>
          </div>
        )}

        <div className="min-h-[22rem] rounded-3xl border border-white/10 bg-white/[0.03] p-5 sm:p-7">
          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={submissionResult ? '__breakdown__' : isComplete ? '__complete__' : current.question.key}
              custom={direction}
              initial={
                shouldReduceMotion
                  ? false
                  : { opacity: 0, x: direction > 0 ? 28 : -28 }
              }
              animate={{ opacity: 1, x: 0 }}
              exit={
                shouldReduceMotion
                  ? undefined
                  : { opacity: 0, x: direction > 0 ? -28 : 28 }
              }
              transition={
                shouldReduceMotion
                  ? { duration: 0 }
                  : { type: 'spring', stiffness: 380, damping: 34 }
              }
            >
              {submissionResult ? (
                <BreakdownStack
                  config={config}
                  snapshot={submissionResult.snapshot}
                  resultsToken={submissionResult.resultsToken}
                />
              ) : isComplete ? (
                <CaptureForm config={config} answers={answers} onSuccess={setSubmissionResult} />
              ) : (
                <QuestionBody
                  current={current}
                  answers={answers}
                  shouldReduceMotion={shouldReduceMotion}
                  onSelectSingle={selectSingle}
                  onToggleMulti={toggleMulti}
                  onSliderChange={setSliderValue}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {!submissionResult && (
          <div className="mt-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={goBack}
              disabled={clampedStep === 0}
              data-testid="calc-back"
              className="min-h-11 shrink-0 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-neutral-300 transition hover:border-white/40 disabled:opacity-40"
            >
              Back
            </button>
            {!isComplete && current.question.type !== 'single' && (
              <button
                type="button"
                onClick={goNext}
                data-testid="calc-next"
                className="min-h-11 shrink-0 rounded-full bg-kingdom-gold px-6 py-2.5 text-sm font-semibold text-neutral-950 transition hover:bg-[#d9bd65]"
              >
                Next
              </button>
            )}
          </div>
        )}
      </div>

      {showEstimatePanel && <EstimatePanel publicConfig={config} answers={answers} />}
    </div>
  )
}

function QuestionBody({ current, answers, shouldReduceMotion, onSelectSingle, onToggleMulti, onSliderChange }) {
  const { question } = current
  const answer = answers[question.key]
  const options = question.options ?? []

  return (
    <div data-testid="calc-question" data-question-key={question.key}>
      <h2 className="font-display text-xl font-medium text-white sm:text-2xl">{question.prompt}</h2>
      {question.help && (
        <p className="mt-2 text-sm leading-relaxed text-white/55">{question.help}</p>
      )}

      {question.type === 'slider' ? (
        <SliderQuestion
          question={question}
          value={typeof answer === 'number' ? answer : (question.sliderMin ?? 0)}
          onChange={(value) => onSliderChange(question.key, value)}
        />
      ) : (
        <div className={`mt-6 ${fieldGap}`}>
          {options.map((option) => {
            const selected =
              question.type === 'multi'
                ? Array.isArray(answer) && answer.includes(option.key)
                : answer === option.key
            return (
              <OptionCard
                key={option.key}
                option={option}
                variant={question.type === 'multi' ? 'multi' : 'single'}
                selected={selected}
                shouldReduceMotion={shouldReduceMotion}
                onSelect={(optionKey) =>
                  question.type === 'multi'
                    ? onToggleMulti(question.key, optionKey)
                    : onSelectSingle(question.key, optionKey)
                }
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

function SliderQuestion({ question, value, onChange }) {
  const min = typeof question.sliderMin === 'number' ? question.sliderMin : 0
  const max = typeof question.sliderMax === 'number' ? question.sliderMax : 100
  const step = typeof question.sliderStep === 'number' && question.sliderStep > 0 ? question.sliderStep : 1
  const option = (question.options ?? [])[0]

  return (
    <div className="mt-8">
      <div className="flex items-baseline justify-between">
        <span className="text-2xl font-semibold text-kingdom-gold" data-testid="calc-slider-value">
          {value}
        </span>
        {option?.label && <span className="text-sm text-white/50">{option.label}</span>}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-4 h-11 w-full cursor-pointer touch-none accent-kingdom-gold"
        aria-label={question.prompt}
      />
      <div className="mt-1 flex justify-between text-xs text-white/40">
        <span>{min}</span>
        <span>{max}</span>
      </div>
      {option?.description && (
        <p className="mt-4 text-sm leading-relaxed text-white/55">{option.description}</p>
      )}
    </div>
  )
}
